
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Settings } from 'lucide-react';
import { PaymentCategory } from '@/types/payment';
import PaymentCategoryForm from './PaymentCategoryForm';
import PaymentSettingsDialog from './PaymentSettingsDialog';
import CategoryCard from './CategoryCard';
import { AVAILABLE_CURRENCIES } from '@/utils/currencyUtils';

interface PaymentMethod {
  id: string;
  name: string;
  label: string;
}

interface PaymentSettings {
  defaultCurrency: string;
  availableCurrencies: string[];
  availablePaymentMethods: PaymentMethod[];
}

interface PaymentCategoryManagerProps {
  categories: PaymentCategory[];
  onUpdateCategory: (categoryId: string, field: string, value: any) => void;
  onAddCategory: (category: Omit<PaymentCategory, 'id'>) => void;
  onRemoveCategory: (categoryId: string) => void;
  onUpdateSettings: (settings: Partial<PaymentSettings>) => void;
  settings: PaymentSettings;
}

const PaymentCategoryManager: React.FC<PaymentCategoryManagerProps> = ({
  categories,
  onUpdateCategory,
  onAddCategory,
  onRemoveCategory,
  onUpdateSettings,
  settings
}) => {
  const { t } = useTranslation('payments');
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);

  // Use the parent's remove handler directly to avoid duplicate DB calls
  const handleRemoveCategory = onRemoveCategory;

  return (
    <>
      <Card>
        <CardHeader className="p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex-1 min-w-0">
              <CardTitle className="flex items-center gap-2 text-sm sm:text-base">
                <Settings className="h-4 w-4 sm:h-5 sm:w-5 flex-shrink-0" />
                <span className="truncate">{t("payments:categories")} & {t("payments:settings")}</span>
              </CardTitle>
              <CardDescription className="text-xs sm:text-sm mt-1">
                {t("payments:management.description")}
              </CardDescription>
            </div>
            <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
              <PaymentCategoryForm
                onAddCategory={onAddCategory}
                settings={{
                  ...settings,
                  availableCurrencies: AVAILABLE_CURRENCIES.map(c => c.code)
                }}
              />
            </div>
          </div>
        </CardHeader>
        <CardContent className="space-y-4 sm:space-y-6 p-4 sm:p-6">
          {categories.length === 0 ? (
            <div className="text-center py-8 text-gray-500 text-sm">
              {t("payments:management.noCategoriesYet")}
            </div>
          ) : (
            categories.map((category) => (
              <CategoryCard
                key={category.id}
                category={category}
                settings={{
                  ...settings,
                  availableCurrencies: AVAILABLE_CURRENCIES.map(c => c.code)
                }}
                onUpdateCategory={onUpdateCategory}
                onRemoveCategory={handleRemoveCategory}
              />
            ))
          )}
        </CardContent>
      </Card>

      <PaymentSettingsDialog
        isOpen={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
      />
    </>
  );
};

export default PaymentCategoryManager;
